/**
 * Image processing utilities for resizing and converting images
 */

export interface ImageProcessingOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: "webp" | "jpeg" | "png";
}

/**
 * Process an image file: resize to fit within dimensions and convert to WebP
 * @param file - The original image file
 * @param options - Processing options (maxWidth, maxHeight, quality, format)
 * @returns Promise with the processed image as a Blob
 */
export async function processImage(
  file: File,
  options: ImageProcessingOptions = {}
): Promise<Blob> {
  const {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.85,
    format = "webp",
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Could not get canvas context"));
      return;
    }

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;

      if (width > maxWidth || height > maxHeight) {
        const widthRatio = maxWidth / width;
        const heightRatio = maxHeight / height;
        const ratio = Math.min(widthRatio, heightRatio);

        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw image on canvas with high quality
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to desired format
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to convert image"));
          }
        },
        `image/${format}`,
        quality
      );
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    // Load the image
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Convert a Blob to a File with a specific name
 */
export function blobToFile(blob: Blob, filename: string): File {
  return new File([blob], filename, { type: blob.type });
}

/**
 * Process image and generate preview with metadata
 * @param file - The original image file
 * @param options - Processing options
 * @returns Promise with processed blob, preview URL, and metadata
 */
export async function processImageWithPreview(
  file: File,
  options: ImageProcessingOptions = {}
): Promise<{
  blob: Blob;
  previewUrl: string;
  originalSize: number;
  processedSize: number;
  dimensions: { width: number; height: number };
}> {
  const blob = await processImage(file, options);
  const previewUrl = URL.createObjectURL(blob);

  // Get dimensions from the processed blob
  const dimensions = await new Promise<{ width: number; height: number }>(
    (resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.src = previewUrl;
    }
  );

  return {
    blob,
    previewUrl,
    originalSize: file.size,
    processedSize: blob.size,
    dimensions,
  };
}
